import { Clickable, Col, Icon, Row, Text } from 'components';
import { POLL_STATE, POLL_TYPE, theme } from 'lib';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

// Match the Quick Quiz / Shoutout card footprint so the profile rows align.
const CARD_WIDTH_DESKTOP = 296;
const CARD_WIDTH_MOBILE = 240;

const Card = styled.div`
  width: ${(p) => (p.isMobile ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP)}px;
  min-width: ${(p) => (p.isMobile ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP)}px;
  background: ${theme.GREY_10};
  border-radius: 12px;
  padding: 14px 14px 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 160px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  background: ${(p) => p.bg};
  color: ${(p) => p.col};
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(39, 227, 163, 0.55); }
  70% { box-shadow: 0 0 0 6px rgba(39, 227, 163, 0); }
  100% { box-shadow: 0 0 0 0 rgba(39, 227, 163, 0); }
`;

const LiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${theme.GREEN};
  animation: ${pulse} 1.6s infinite;
`;

const Question = styled.p`
  font-family: CeraPro;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  color: ${theme.GREY_90};
  margin: 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PointsPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  color: ${theme.GREY_70};
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
`;

const ActionBtn = styled.button`
  border: none;
  padding: 6px 14px;
  border-radius: 8px;
  font-family: CeraPro;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.tone === 'primary' ? theme.SECONDARY : '#fff')};
  color: ${(p) => (p.tone === 'primary' ? '#fff' : theme.GREY_80)};
  border: 1px solid ${(p) => (p.tone === 'primary' ? theme.SECONDARY : theme.GREY_20)};
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 6px;
  color: ${(p) => (p.danger ? theme.SECONDARY : theme.GREY_60)};
  &:hover {
    background: #fff;
  }
`;

// Surface state → chip. `open` is rendered as a separate LIVE badge, so this
// returns null for it. A future-dated active poll reads as "Scheduled" rather
// than the bare "Hidden" the resolver returns, which is clearer for admins.
const stateChip = (poll, t) => {
  const state = poll.state;
  if (state === POLL_STATE.CLOSED) {
    return { label: t('pollQuizProfile.chipClosed'), bg: theme.GREY_10, col: theme.GREY_60 };
  }
  if (state === POLL_STATE.HIDDEN) {
    const future = poll.startDate && new Date(poll.startDate).getTime() > Date.now();
    if (future && poll.isActive !== false) {
      return { label: t('pollQuizProfile.chipScheduled'), bg: `${theme.PURPLE}20`, col: theme.PURPLE };
    }
    return { label: t('pollQuizProfile.chipHidden'), bg: `${theme.ORANGE}20`, col: theme.ORANGE };
  }
  return null;
};

// Read-only profile card for a single poll. Admins get edit/delete/toggle icon
// buttons; fans get a single "View" / "View results" button (voting is in the
// mobile app per the brief — the web card never submits a vote).
const PollProfileCard = ({ poll, isMobile, canManage, onView, onEdit, onDelete, onToggleActive }) => {
  const { t } = useTranslation();
  const state = poll.state;
  const isLive = state === POLL_STATE.OPEN && poll.isActive !== false;
  const chip = stateChip(poll, t);
  const isAuto = poll.source === 'auto';
  const isGameDay = poll.pollType === POLL_TYPE.GAME_DAY;
  // Fans can vote on an open poll they haven't answered; otherwise it's view-only.
  const fanCanVote = state === POLL_STATE.OPEN && poll.answered !== true;

  const handleCardClick = () => {
    if (canManage) onEdit?.(poll);
    else onView?.(poll);
  };

  return (
    <Card isMobile={isMobile} onClick={handleCardClick}>
      <Col noFlex>
        <Row between center>
          {isLive ? (
            <Chip bg={`${theme.GREEN}20`} col={theme.GREEN}>
              <LiveDot /> {t('pollQuizProfile.live')}
            </Chip>
          ) : chip ? (
            <Chip bg={chip.bg} col={chip.col}>
              {chip.label}
            </Chip>
          ) : (
            <span />
          )}

          <Row noFlex center gap="6px">
            {isAuto && (
              <Chip bg={`${theme.TALE}25`} col={theme.GREY_70}>
                {t('pollQuizProfile.chipAuto')}
              </Chip>
            )}
            {canManage && poll.isActive === false && (
              <Chip bg={theme.GREY_10} col={theme.GREY_60}>
                {t('pollQuizProfile.inactive')}
              </Chip>
            )}
          </Row>
        </Row>

        <Question>{poll.question}</Question>
      </Col>

      <Row between center>
        <Row noFlex center gap="8px">
          <PointsPill>
            <span style={{ color: theme.SECONDARY, fontSize: 12, lineHeight: 1 }}>★</span>
            {t('pollQuizProfile.pts', { count: poll.rewardPoints || 0 })}
          </PointsPill>
          <Text c2 col={theme.GREY_50}>
            {isGameDay ? t('pollQuizProfile.gameDay') : t('pollQuizProfile.pollLabel')}
          </Text>
        </Row>

        {canManage ? (
          <Row noFlex center gap="4px">
            <IconBtn
              type="button"
              title={poll.isActive ? t('pollQuizProfile.deactivate') : t('pollQuizProfile.activate')}
              onClick={(e) => {
                e.stopPropagation();
                onToggleActive?.(poll);
              }}
            >
              <Icon type={poll.isActive ? 'pause' : 'play'} dimensions={14} />
            </IconBtn>
            <IconBtn
              type="button"
              title={t('pollQuizProfile.viewResults')}
              onClick={(e) => {
                e.stopPropagation();
                onView?.(poll);
              }}
            >
              <Icon type="barChart" dimensions={14} col="currentColor" />
            </IconBtn>
            <IconBtn
              type="button"
              danger
              title={t('pollQuizProfile.delete')}
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(poll);
              }}
            >
              <Icon type="delete" dimensions={14} col="currentColor" />
            </IconBtn>
            <Clickable
              onClick={(e) => {
                e?.stopPropagation();
                onEdit?.(poll);
              }}
            >
              <Text c2 bold col={theme.SECONDARY}>
                {t('pollQuizProfile.edit')}
              </Text>
            </Clickable>
          </Row>
        ) : (
          <ActionBtn
            tone={fanCanVote ? 'primary' : 'muted'}
            onClick={(e) => {
              e.stopPropagation();
              onView?.(poll);
            }}
          >
            {fanCanVote ? t('pollQuizProfile.voteNow') : t('pollQuizProfile.viewResults')}
          </ActionBtn>
        )}
      </Row>
    </Card>
  );
};

export default memo(PollProfileCard);
